import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Message, AgentWithIcon } from '../../types/chat';
import NewChatMessage from './NewChatMessage';
import { clearAnimation } from '../../utils/animationManager';
import { useSearchProgress } from '../../hooks/useSearchProgress';
import SearchProgressBox from './SearchProgressBox';
import ThinkingDisplay from './ThinkingDisplay';
import { ScrollArea } from '../ui/scroll-area';
import { ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';

interface NewChatMessagesProps {
  messages: Message[];
  isLoading?: boolean;
  selectedAgent?: AgentWithIcon | null;
  isNewChat?: boolean;
  chatId?: string;
  chats?: any[];
  showThinkingProcess?: boolean;
  thinkingMessage?: string;
  onThinkingProcessClose?: () => void;
  onRegenerateMessage?: (messageId: number) => void;
  isRegenerating?: boolean;
  regeneratingMessageId?: number | null;
  // GPT-5 AIのthinking（reasoning summary）内容
  aiThinkingContent?: string;
  onToolAction?: (text: string) => void;
  toolActionsDisabled?: boolean;
}

const NewChatMessages: React.FC<NewChatMessagesProps> = ({
  messages,
  isLoading = false,
  selectedAgent = null,
  isNewChat = false,
  chatId,
  chats = [],
  showThinkingProcess = false,
  thinkingMessage = '',
  onThinkingProcessClose,
  onRegenerateMessage,
  isRegenerating = false,
  regeneratingMessageId = null,
  aiThinkingContent = '',
  onToolAction,
  toolActionsDisabled = false,
}) => {
  // 提案カードを「実行済み」にするため、全メッセージの tool_calls から消費済み確認コードを集める。
  const executedProposalCodes = React.useMemo(
    () => messages.flatMap((m) => (m.tool_calls ?? []).map((tc) => tc.proposal_code).filter((c): c is string => Boolean(c))),
    [messages]
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 現在アニメーション中のメッセージIDを保持
  const [animatingMessageId, setAnimatingMessageId] = useState<number | null>(null);

  // スクロール位置に基づいてボタンを表示するか
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);

  // 処理中のメッセージIDを取得
  const processingMessage = messages.find(msg => msg.status === 'processing');
  const { searchProgress, resetProgress, disconnect } = useSearchProgress(
    chatId ? Number(chatId) : 0,
    processingMessage?.id || 0
  );

  // スクロールブロックフラグ
  const scrollBlockedRef = useRef<boolean>(false);

  // 初期ロード済みかどうかのフラグ
  const initialLoadDoneRef = useRef<boolean>(false);

  // これまでに表示したすべてのメッセージIDを保持
  const processedMessageIdsRef = useRef<Set<number>>(new Set());

  // スクロール処理
  const scrollToBottom = useCallback(() => {
    if (scrollBlockedRef.current) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest'
    });

    setTimeout(() => {
      if (!scrollBlockedRef.current) {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'end',
          inline: 'nearest'
        });
      }
    }, 100);
  }, []);

  // コンポーネントのマウント時に初期化処理
  useEffect(() => {
    return () => {
      processedMessageIdsRef.current.clear();
      initialLoadDoneRef.current = false;
      clearAnimation();
    };
  }, []);

  // メッセージの変更を監視
  useEffect(() => {
    // 初期表示時（既存チャットの読み込み）のみ下部にスクロール
    if (messages.length > 0 && !initialLoadDoneRef.current) {
      scrollToBottom();

      messages.forEach(msg => {
        processedMessageIdsRef.current.add(msg.id);
      });
      initialLoadDoneRef.current = true;
      return;
    }

    // 新しく追加されたメッセージを検出
    for (const message of messages) {
      if (!processedMessageIdsRef.current.has(message.id)) {
        processedMessageIdsRef.current.add(message.id);

        // ユーザーメッセージが追加されたら、一番下にスクロール
        if (message.sender_type === 'user') {
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }

        // AIからの返信メッセージの場合のみアニメーション
        if (message.sender_type === 'ai') {
          setAnimatingMessageId(null);

          setTimeout(() => {
            setAnimatingMessageId(message.id);
          }, 200);
        }
      }
    }
  }, [messages, scrollToBottom]);

  // アニメーション完了時のハンドラー
  const handleAnimationComplete = useCallback(() => {
    const currentAnimatingId = animatingMessageId;

    if (currentAnimatingId === null) {
      return;
    }

    setTimeout(() => {
      scrollBlockedRef.current = false;

      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'end',
          inline: 'nearest'
        });
      }, 100);
    }, 300);
  }, [animatingMessageId]);

  // スクロール位置を監視してボタンの表示/非表示を切り替え
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollElement = container.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!scrollElement) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
    setShowScrollButton(!isNearBottom);
  }, []);

  // スクロールイベントリスナーを設定
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollElement = container.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!scrollElement) return;

    scrollElement.addEventListener('scroll', checkScrollPosition);
    checkScrollPosition();

    return () => {
      scrollElement.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  // ボタンクリック時のスクロール処理
  const handleScrollToBottomClick = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest'
    });
  }, []);

  // AI応答中かどうか（Thinking表示の判定用）
  // processing状態かつcontentが空のAIメッセージがあるかどうかをチェック
  const hasProcessingAIMessageWithoutContent = messages.some(msg => msg.sender_type === 'ai' && msg.status === 'processing' && !msg.content && !(msg.tool_calls && msg.tool_calls.length > 0));
  const isWaitingForAI = isLoading || hasProcessingAIMessageWithoutContent;

  return (
    <div className="flex-1 flex flex-col min-h-0 relative">
      <ScrollArea ref={scrollContainerRef} className="flex-1 px-4">
        <div className="max-w-4xl mx-auto py-6 space-y-6">
          {/* 重複を除去してから表示 */}
          {(() => {
            const uniqueMessages: typeof messages = [];
            const seenIds = new Set();
            for (const message of messages) {
              if (!seenIds.has(message.id)) {
                seenIds.add(message.id);
                uniqueMessages.push(message);
              }
            }
            return uniqueMessages;
          })().map((message, index, arr) => {
            // processingステータスでもcontentがあれば表示する（ストリーミング用）
            // contentが空でもツール実行カードがあれば表示する（ツール実行フェーズ）
            // contentもツールも無い場合のみスキップ（応答待ち表示が代わりに表示される）
            if (message.status === 'processing' && !message.content && !(message.tool_calls && message.tool_calls.length > 0)) {
              return null;
            }

            // アニメーション対象のメッセージかチェック
            const isAnimating = message.id === animatingMessageId && message.sender_type === 'ai';

            // 最後のメッセージがprocessing中のAIメッセージの場合、min-heightを適用
            const isLastMessage = index === arr.length - 1;
            const isProcessingAI = message.sender_type === 'ai' && message.status === 'processing';
            const shouldApplyMinHeight = isLastMessage && isProcessingAI;

            // 最後のユーザーメッセージかつAI応答待ちの場合、Thinking表示エリアを追加
            // processingで空のAIメッセージは表示されないので、実質的に最後に表示されるメッセージを判定
            const isLastVisibleUserMessage = message.sender_type === 'user' &&
              (index === arr.length - 1 ||
               (index === arr.length - 2 && arr[arr.length - 1]?.sender_type === 'ai' && arr[arr.length - 1]?.status === 'processing' && !arr[arr.length - 1]?.content && !(arr[arr.length - 1]?.tool_calls && (arr[arr.length - 1]?.tool_calls?.length ?? 0) > 0)));
            const shouldShowThinkingArea = isLastVisibleUserMessage && isWaitingForAI;

            return (
              <div key={message.id} className={shouldApplyMinHeight ? 'min-h-[calc(100dvh-380px)]' : ''}>
                {/* メッセージ本体 */}
                <div className="space-y-4">
                  <NewChatMessage
                    message={{
                      id: message.id,
                      role: message.sender_type === 'user' ? 'user' : 'assistant',
                      content: message.content,
                      timestamp: message.created_at,
                      status: message.status,
                      attachments: message.attachments,
                      uploaded_files: message.uploaded_files,
                      suggestions: message.suggestions,
                      search_metadata: message.search_metadata,
                      tool_calls: message.tool_calls
                    }}
                    isLatestMessage={isAnimating}
                    onAnimationComplete={isAnimating ? handleAnimationComplete : undefined}
                    onRegenerate={message.sender_type === 'ai' ? onRegenerateMessage : undefined}
                    isRegenerating={isRegenerating && message.id === regeneratingMessageId}
                    onToolAction={onToolAction}
                    toolActionsDisabled={toolActionsDisabled}
                    executedProposalCodes={executedProposalCodes}
                  />
                </div>

                {/* AI回答エリア（最後のユーザーメッセージの直後にmin-height付きで配置） */}
                {shouldShowThinkingArea && (
                  <div className="min-h-[calc(100dvh-380px)] mt-6 space-y-3">
                    <div className="flex items-center space-x-2">
                      <img src="/faviconV2.png" alt="Tasky" width={16} height={16} className="flex-shrink-0 opacity-80" />
                      <span className="text-sm font-semibold text-gray-500">
                        {"Tasky Thinking...".split("").map((char, charIndex) => (
                          <span
                            key={charIndex}
                            className="inline-block"
                            style={{
                              animation: 'shimmer 1.5s ease-in-out infinite',
                              animationDelay: `${charIndex * 0.05}s`,
                            }}
                          >
                            {char === " " ? "\u00A0" : char}
                          </span>
                        ))}
                      </span>
                    </div>

                    {/* タスク進行状況の表示 */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      {searchProgress.isActive ? (
                        <SearchProgressBox
                          isVisible={searchProgress.isActive}
                          queries={searchProgress.queries}
                          results={searchProgress.results}
                          status={searchProgress.status}
                        />
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2 mb-3">
                            <span className="font-medium text-gray-900">実行中のタスク</span>
                          </div>
                          <div className="space-y-2.5">
                            <div className="flex items-center space-x-3 text-sm">
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin flex-shrink-0"></div>
                              <span className="text-gray-700">AIが応答を作成中...</span>
                            </div>
                          </div>
                          {/* GPT-5のthinking（reasoning summary）表示 */}
                          {aiThinkingContent && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <ThinkingDisplay
                                content={aiThinkingContent}
                                isStreaming={isLoading}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <style>{`
                      @keyframes shimmer {
                        0%, 100% { opacity: 0.4; }
                        50% { opacity: 1; }
                      }
                    `}</style>
                  </div>
                )}
              </div>
            );
          })}

          {/* スクロールターゲット用の要素 */}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* 一番下にスクロールするボタン */}
      {showScrollButton && (
        <div className="absolute bottom-6 right-6 z-10">
          <Button
            onClick={handleScrollToBottomClick}
            className="rounded-full w-10 h-10 p-0 shadow-lg bg-white hover:bg-gray-100 border border-gray-300"
            variant="ghost"
            title="一番下にスクロール"
          >
            <ArrowDown className="w-5 h-5 text-gray-700" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default NewChatMessages;
